import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey, ADMIN_ENV_STORAGE_KEY } from "../config/firebase";
import { readSupportSearchDestination, supportSearchEnvironment } from "./supportSearchResults";
import RestaurantFeatureControls from "./RestaurantFeatureControls";
import RestaurantAdvancedSettings from "./RestaurantAdvancedSettings";
import RestaurantPaymentSettings from "./RestaurantPaymentSettings";
import "./styles/RestaurantDetails.css";

const TABS = [
	"overview",
	"profile",
	"features",
	"menu",
	"orders",
	"reservations",
	"operations",
	"owner",
	"audit",
	"advanced settings",
];

const formatMoney = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const formatDate = (value) => {
	if (!value) return "--";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const getFeatureCount = (features = {}) =>
	Object.values(features).filter((value) => value !== false).length;

const terminalField = field => /terminal|collector|readerAccountScope|usePlatformTerminalAccount/i.test(field);
const withoutTerminalFields = value => Object.fromEntries(Object.entries(value || {}).filter(([field]) => !terminalField(field)));
// Generic profile snapshots must never undo setup verified by the dedicated callable.
const profileUpdates = formData => ({
	...withoutTerminalFields(formData),
	restaurantNumber: Number(formData.restaurantNumber || 0),
	taxRate: Number(formData.taxRate || 0),
	payLitePolicy: withoutTerminalFields(formData.payLitePolicy),
	...(formData.paymentPolicy ? { paymentPolicy: withoutTerminalFields(formData.paymentPolicy) } : {}),
});
const mergeTerminalPatch = (current, saved) => ({ ...current,
	...Object.fromEntries(Object.entries(saved).filter(([field]) => terminalField(field))),
	payLitePolicy: { ...current?.payLitePolicy, ...Object.fromEntries(Object.entries(saved.payLitePolicy || {}).filter(([field]) => terminalField(field))) },
});

const getListingLabel = (restaurant = {}) => {
	const status =
		restaurant.listingStatus || restaurant.scervStatus || restaurant.claimStatus;
	if (status === "community") return "Community listed";
	if (status === "claimed") return "Claimed";
	if (status === "scerv_enabled") return "Scerv enabled";
	return "Scerv enabled";
};

const getOperationalLabel = (restaurant = {}) => {
	const status =
		restaurant.claimWorkflow?.operationalActivationStatus ||
		restaurant.listingStatus ||
		restaurant.scervStatus ||
		restaurant.claimStatus;
	if (status === "claimed_discovery_only" || status === "claimed") {
		return "Claimed, discovery only";
	}
	if (status === "community") return "Unclaimed discovery profile";
	if (status === "scerv_enabled") return "Scerv tools enabled";
	return "Operational status pending";
};

const getClaimChecklist = (restaurant = {}, owner = {}) => {
	const status = restaurant.listingStatus || restaurant.scervStatus;
	return [
		{
			label: "Community profile created",
			done: Boolean(restaurant.isCommunityProfile || restaurant.isClaimed),
		},
		{
			label: "Owner assigned",
			done: Boolean(restaurant.ownerUid || restaurant.uid || owner?.email),
		},
		{
			label: "Setup link sent",
			done: Boolean(restaurant.ownerSetupLastSentAt || restaurant.claimedAt),
		},
		{
			label: "Operational features enabled",
			done: status === "scerv_enabled",
		},
	];
};

const RestaurantDetailsContent = ({ id, initialTab = "overview", initialSection }) => {
	const [profile, setProfile] = useState(null);
	const [restaurant, setRestaurant] = useState(null);
	const [formData, setFormData] = useState({});
	const [activeTab, setActiveTab] = useState(initialTab);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [setupResult, setSetupResult] = useState(null);
	const [claimForm, setClaimForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phoneNumber: "",
		emailOwner: true,
		enableScerv: false,
	});

	const loadProfile = async () => {
		setLoading(true);
		setError("");
		try {
			const getProfile = httpsCallable(functions, "getScervRestaurantProfile");
			const response = await getProfile({ restaurantId: id });
			const data = response.data || {};
			const restaurantData = data.restaurant || {};
			const ownerData = data.owner || {};
			setProfile(data);
			setRestaurant(restaurantData);
			setFormData(restaurantData);
			setClaimForm((prev) => ({
				...prev,
				firstName:
					ownerData.firstName || restaurantData.firstName || prev.firstName || "",
				lastName:
					ownerData.lastName || restaurantData.lastName || prev.lastName || "",
				email:
					ownerData.email ||
					restaurantData.email ||
					restaurantData.claimContactEmail ||
					prev.email ||
					"",
				phoneNumber:
					ownerData.phoneNumber ||
					restaurantData.phoneNumber ||
					prev.phoneNumber ||
					"",
				enableScerv:
					(restaurantData.listingStatus || restaurantData.scervStatus) ===
					"scerv_enabled",
			}));
		} catch (err) {
			console.error("Error fetching restaurant data:", err);
			setError("Error fetching restaurant data.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadProfile();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const orders = useMemo(() => profile?.orders || [], [profile]);
	const reservations = useMemo(() => profile?.reservations || [], [profile]);
	const checkIns = useMemo(() => profile?.checkIns || [], [profile]);
	const tables = useMemo(() => profile?.tables || [], [profile]);
	const employees = useMemo(() => profile?.employees || [], [profile]);
	const menuItems = useMemo(() => profile?.menuItems || [], [profile]);
	const owner = useMemo(() => profile?.owner || {}, [profile]);
	const entitlements = restaurant?.featureEntitlements || {};
	const listingStatus = restaurant?.listingStatus || restaurant?.scervStatus || "scerv_enabled";
	const claimChecklist = useMemo(
		() => getClaimChecklist(restaurant || {}, owner),
		[restaurant, owner],
	);

	const orderTotals = useMemo(
		() =>
			orders.reduce(
				(acc, order) => {
					acc.total += Number(order.totalPrice || 0);
					acc.refunded += Number(order.refundedAmount || 0);
					return acc;
				},
				{ total: 0, refunded: 0 },
			),
		[orders],
	);

	const handleChange = (event) => {
		const { name, value, type, checked } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const updatePayLitePolicy = (field, value) => {
		setFormData((prev) => ({
			...prev,
			payLitePolicy: {
				...(prev.payLitePolicy || {}),
				[field]: value,
			},
		}));
	};

	const saveProfile = async (event) => {
		event.preventDefault();
		setSaving(true);
		setError("");
		setMessage("");

		try {
			const updateProfile = httpsCallable(
				functions,
				"updateScervRestaurantProfile",
			);
			const updates = profileUpdates(formData);
			await updateProfile({ restaurantId: id, updates });
			setRestaurant(updates);
			setMessage("Restaurant profile saved.");
			await loadProfile();
		} catch (err) {
			console.error("Error updating restaurant:", err);
			setError("Error updating restaurant.");
		} finally {
			setSaving(false);
		}
	};

	const resendOwnerSetup = async () => {
		setSaving(true);
		setError("");
		setMessage("");
		setSetupResult(null);

		try {
			const resendSetup = httpsCallable(
				functions,
				"resendRestaurantOwnerSetupEmail",
			);
			const response = await resendSetup({ restaurantId: id });
			setSetupResult(response.data);
			setMessage("Owner setup link generated.");
		} catch (err) {
			console.error("Error resending owner setup:", err);
			setError("Could not generate owner setup link.");
		} finally {
			setSaving(false);
		}
	};

	const assignOwner = async (event) => {
		event.preventDefault();
		setSaving(true);
		setError("");
		setMessage("");
		setSetupResult(null);

		try {
			const assignRestaurantOwner = httpsCallable(
				functions,
				"assignScervRestaurantOwner",
			);
			const response = await assignRestaurantOwner({
				restaurantId: id,
				owner: {
					firstName: claimForm.firstName,
					lastName: claimForm.lastName,
					email: claimForm.email,
					phoneNumber: claimForm.phoneNumber,
				},
				emailOwner: claimForm.emailOwner,
				enableScerv: claimForm.enableScerv,
			});
			setSetupResult(response.data);
			setMessage("Owner assigned and setup link generated.");
			await loadProfile();
		} catch (err) {
			console.error("Error assigning owner:", err);
			setError(err.message || "Could not assign owner.");
		} finally {
			setSaving(false);
		}
	};

	if (loading && !restaurant) return <div className="restaurant-details-container">Loading...</div>;
	if (error && !restaurant) {
		return <div className="restaurant-details-container error">{error}</div>;
	}
	if (!restaurant) {
		return <div className="restaurant-details-container">Restaurant not found.</div>;
	}

	return (
		<div className="restaurant-details-container" aria-busy={loading}>
			<div className="restaurant-hero">
				<div>
					<Link to="/restaurants">Back to restaurants</Link>
					<h1>{restaurant.restaurantName || "Restaurant"}</h1>
					<p>
						Store #{restaurant.restaurantNumber || "--"} -{" "}
						{restaurant.city || "--"}, {restaurant.state || "--"}
					</p>
				</div>
				<div className="restaurant-status-stack">
					<span className={restaurant.isActive ? "good-pill" : "bad-pill"}>
						{restaurant.isActive ? "Active" : "Inactive"}
					</span>
					<span className={restaurant.isLive ? "good-pill" : "warn-pill"}>
						{restaurant.isLive ? "Live" : "Not live"}
					</span>
					<span
						className={
							restaurant.isCustomerVisible === false ? "warn-pill" : "good-pill"
						}
					>
						{restaurant.isCustomerVisible === false
							? "Customer hidden"
							: "Customer visible"}
					</span>
					<span className="neutral-pill">
						{restaurant.planLevel || restaurant.subscription?.planLevel || "starter"}
					</span>
					<span className={`listing-pill listing-${listingStatus}`}>
						{getListingLabel(restaurant)}
					</span>
				</div>
			</div>

			{message && <p className="restaurant-message">{message}</p>}
			{error && <p className="restaurant-error">{error}</p>}

			<div className="restaurant-tabs">
				{TABS.map((tab) => (
					<button
						key={tab}
						type="button"
						className={activeTab === tab ? "active" : ""}
						onClick={() => setActiveTab(tab)}
					>
						{tab}
					</button>
				))}
			</div>

			{activeTab === "advanced settings" && (
				<RestaurantAdvancedSettings
					key={id}
					restaurantId={id}
					initialSection={initialSection}
					onPrinterSaved={(saved) => {
						setRestaurant((current) => ({ ...current, ...saved }));
						setFormData((current) => ({ ...current, ...saved }));
					}}
					onTerminalSaved={(saved) => {
						setRestaurant((current) => mergeTerminalPatch(current, saved));
						setFormData((current) => mergeTerminalPatch(current, saved));
					}}
					paymentSettings={(
						<RestaurantPaymentSettings
							formData={formData}
							updatePayLitePolicy={updatePayLitePolicy}
							saveProfile={saveProfile}
							saving={saving}
						/>
					)}
				/>
			)}
			{activeTab === "overview" && (
				<div className="restaurant-grid">
					<section className="restaurant-panel">
						<h2>Readiness</h2>
						<dl>
							<dt>Stripe</dt>
							<dd>{restaurant.stripeAccountStatus || "unverified"}</dd>
							<dt>Onboarding</dt>
							<dd>{restaurant.onboardingStatus || "--"}</dd>
							<dt>Subscription</dt>
							<dd>{restaurant.subscriptionStatus || restaurant.subscription?.status || "--"}</dd>
							<dt>Features enabled</dt>
							<dd>{getFeatureCount(entitlements)}</dd>
						</dl>
					</section>
					<section className="restaurant-panel">
						<h2>Activity Snapshot</h2>
						<div className="metric-grid">
							<div><strong>{menuItems.length}</strong><span>Menu items</span></div>
							<div><strong>{orders.length}</strong><span>Recent orders</span></div>
							<div><strong>{reservations.length}</strong><span>Reservations</span></div>
							<div><strong>{checkIns.length}</strong><span>Check-ins</span></div>
						</div>
					</section>
					<section className="restaurant-panel">
						<h2>Money Snapshot</h2>
						<div className="metric-grid">
							<div><strong>{formatMoney(orderTotals.total)}</strong><span>Recent paid</span></div>
							<div><strong>{formatMoney(orderTotals.refunded)}</strong><span>Refunded</span></div>
						</div>
					</section>
					<section className="restaurant-panel">
						<h2>Shortcuts</h2>
						<div className="shortcut-row">
							<Link to={`/restaurants/${id}/menu`}>Manage Menu</Link>
							<button
								type="button"
								onClick={() => setActiveTab("owner")}
							>
								Claim Setup
							</button>
							<Link to="/command-center">Command Center</Link>
						</div>
					</section>
					<section className="restaurant-panel wide-panel">
						<h2>Claim Readiness</h2>
						<div className="claim-status-grid">
							<div>
								<span>Listing</span>
								<strong>{getListingLabel(restaurant)}</strong>
							</div>
							<div>
								<span>Operations</span>
								<strong>{getOperationalLabel(restaurant)}</strong>
							</div>
							<div>
								<span>Ratings</span>
								<strong>{restaurant.ratingCount || 0}</strong>
							</div>
							<div>
								<span>Menu items</span>
								<strong>{menuItems.length}</strong>
							</div>
						</div>
					</section>
				</div>
			)}

			{activeTab === "profile" && (
				<section className="restaurant-panel">
					<h2>Profile</h2>
					<form className="restaurant-profile-form" onSubmit={saveProfile}>
						{[
							["restaurantName", "Restaurant name"],
							["restaurantNumber", "Store #"],
							["address", "Address"],
							["city", "City"],
							["state", "State"],
							["zipcode", "Zip code"],
							["phoneNumber", "Phone"],
							["email", "Email"],
							["cuisineType", "Cuisine"],
							["taxRate", "Tax rate"],
							["website", "Website"],
							["imageUri", "Image URL"],
						].map(([field, label]) => (
							<label key={field}>
								{label}
								<input
									name={field}
									value={formData[field] || ""}
									onChange={handleChange}
								/>
							</label>
						))}
						<label className="wide-field">
							Description
							<textarea
								name="description"
								value={formData.description || ""}
								onChange={handleChange}
							/>
						</label>
						<label>
							Listing status
							<select
								name="listingStatus"
								value={formData.listingStatus || formData.scervStatus || "scerv_enabled"}
								onChange={(event) => {
									handleChange(event);
									setFormData((prev) => ({
										...prev,
										scervStatus: event.target.value,
										claimStatus:
											event.target.value === "community"
												? "unclaimed"
												: "claimed",
									}));
								}}
							>
								<option value="community">Community listed</option>
								<option value="claimed">Claimed</option>
								<option value="scerv_enabled">Scerv enabled</option>
							</select>
						</label>
						<label className="check-field">
							<input
								type="checkbox"
								name="isActive"
								checked={formData.isActive !== false}
								onChange={handleChange}
							/>
							Active account
						</label>
						<label className="check-field">
							<input
								type="checkbox"
								name="isLive"
								checked={formData.isLive === true}
								onChange={handleChange}
							/>
							Live operations / payments enabled
						</label>
						<label className="check-field">
							<input
								type="checkbox"
								name="isCustomerVisible"
								checked={formData.isCustomerVisible !== false}
								onChange={handleChange}
							/>
							Customer-visible listing
							<span className="field-help">
								Turn this off for Pay Lite-only restaurants that should not appear
								in customer discovery or public restaurant pages yet.
							</span>
						</label>
						<button type="submit" disabled={saving}>
							{saving ? "Saving..." : "Save Profile"}
						</button>
					</form>
				</section>
			)}

			{activeTab === "features" && (
				<RestaurantFeatureControls
					restaurantId={id}
					restaurant={restaurant}
					onSaved={(updatedRestaurant) => {
						setRestaurant(updatedRestaurant);
						setFormData(updatedRestaurant);
					}}
				/>
			)}

			{activeTab === "menu" && (
				<section className="restaurant-panel">
					<h2>Menu</h2>
					<Link className="primary-link" to={`/restaurants/${id}/menu`}>
						Manage Menu
					</Link>
					<table className="restaurant-table">
						<thead><tr><th>Item</th><th>Category</th><th>Tags</th><th>Rating</th><th>Status</th></tr></thead>
						<tbody>
							{menuItems.slice(0, 25).map((item) => (
								<tr key={item.id}>
									<td>{item.name}</td>
									<td>{item.category || "--"}</td>
									<td>
										{[
											...(item.tags || []),
											...(item.cuisineTags || []),
											...(item.dietaryTags || []),
										].slice(0, 4).join(", ") || "--"}
									</td>
									<td>
										{Number(item.averageRating || item.rating || 0).toFixed(1)} /{" "}
										{item.ratingCount || 0}
									</td>
									<td>{item.isArchived ? "Archived" : item.isActive ? "Active" : "Inactive"}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			)}

			{activeTab === "orders" && (
				<section className="restaurant-panel">
					<h2>Recent Orders</h2>
					<table className="restaurant-table">
						<thead><tr><th>Order</th><th>Customer</th><th>Status</th><th>Total</th></tr></thead>
						<tbody>
							{orders.map((order) => (
								<tr key={order.id}>
									<td><Link to={`/orders/${order.id}`}>{order.readableOrderId || order.id}</Link></td>
									<td>{order.customerName || order.customerEmail || "--"}</td>
									<td>{order.paymentStatus || order.orderStatus || "--"}</td>
									<td>{formatMoney(order.totalPrice)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			)}

			{activeTab === "reservations" && (
				<section className="restaurant-panel">
					<h2>Reservations</h2>
					<table className="restaurant-table">
						<thead><tr><th>Guest</th><th>Status</th><th>Party</th><th>Time</th></tr></thead>
						<tbody>
							{reservations.map((reservation) => (
								<tr key={reservation.id}>
									<td>{reservation.customerName || reservation.customerEmail || "--"}</td>
									<td>{reservation.status || "--"}</td>
									<td>{reservation.partySize || "--"}</td>
									<td>{reservation.reservationTime || formatDate(reservation.reservationDate)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			)}

			{activeTab === "operations" && (
				<div className="restaurant-grid">
					<section className="restaurant-panel">
						<h2>Tables</h2>
						<table className="restaurant-table">
							<tbody>
								{tables.slice(0, 30).map((table) => (
									<tr key={table.id}><td>{table.name || table.tableNumber || table.id}</td><td>{table.status || "--"}</td></tr>
								))}
							</tbody>
						</table>
					</section>
					<section className="restaurant-panel">
						<h2>Employees ({employees.length})</h2>
						<table className="restaurant-table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Role</th>
									<th>Status</th>
									<th>ID</th>
								</tr>
							</thead>
							<tbody>
								{employees.length === 0 ? (
									<tr>
										<td colSpan="4">No employee records found.</td>
									</tr>
								) : (
									employees.slice(0, 30).map((employee) => (
										<tr key={employee.id}>
											<td>
												{employee.name ||
													`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
													employee.id}
												{employee.uid ? (
													<div className="muted-small">UID: {employee.uid}</div>
												) : null}
											</td>
											<td>{employee.role || employee.jobTitle || "--"}</td>
											<td>{employee.isActive === false ? "Inactive" : "Active"}</td>
											<td><code>{employee.id}</code></td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</section>
					<section className="restaurant-panel wide-panel">
						<h2>Recent Check-ins</h2>
						<table className="restaurant-table">
							<tbody>
								{checkIns.slice(0, 30).map((checkIn) => (
									<tr key={checkIn.id}><td>{checkIn.customerName || checkIn.customerId || checkIn.id}</td><td>{checkIn.status || "--"}</td><td>{formatDate(checkIn.createdAt)}</td></tr>
								))}
							</tbody>
						</table>
					</section>
				</div>
			)}

			{activeTab === "owner" && (
				<section className="restaurant-panel">
					<div className="owner-setup-header">
						<div>
							<h2>Claim Flow</h2>
							<p>
								Assign a verified owner without deleting menu items, ratings,
								reviews, or discovery media.
							</p>
						</div>
						<span className={`listing-pill listing-${listingStatus}`}>
							{getListingLabel(restaurant)}
						</span>
					</div>
					<div className="claim-status-grid">
						<div>
							<span>Claim state</span>
							<strong>{restaurant.claimStatus || "unclaimed"}</strong>
						</div>
						<div>
							<span>Operational state</span>
							<strong>{getOperationalLabel(restaurant)}</strong>
						</div>
						<div>
							<span>Ratings preserved</span>
							<strong>{restaurant.ratingCount || 0}</strong>
						</div>
						<div>
							<span>Reviews preserved</span>
							<strong>{restaurant.reviewCount || 0}</strong>
						</div>
					</div>
					<div className="claim-checklist">
						{claimChecklist.map((step) => (
							<div key={step.label} className={step.done ? "done" : ""}>
								<span>{step.done ? "Done" : "Todo"}</span>
								{step.label}
							</div>
						))}
					</div>
					<dl>
						<dt>Listing</dt><dd>{getListingLabel(restaurant)}</dd>
						<dt>Claim status</dt><dd>{restaurant.claimStatus || "--"}</dd>
						<dt>Claim method</dt><dd>{restaurant.claimWorkflow?.method || "--"}</dd>
						<dt>Name</dt><dd>{owner.fullName || `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || "--"}</dd>
						<dt>Email</dt><dd>{owner.email || restaurant.email || "--"}</dd>
						<dt>Phone</dt><dd>{owner.phoneNumber || restaurant.phoneNumber || "--"}</dd>
						<dt>Owner UID</dt><dd>{restaurant.ownerUid || restaurant.uid || "--"}</dd>
						<dt>Restaurant ID</dt><dd>{id}</dd>
					</dl>
					<form className="owner-claim-form" onSubmit={assignOwner}>
						<h3>
							{restaurant.isClaimed ? "Update Verified Owner" : "Assign Verified Owner"}
						</h3>
						<p className="owner-claim-help">
							Claiming keeps the restaurant visible for discovery and keeps all
							existing reputation attached. Only choose Scerv enabled when the
							restaurant should receive operational tools.
						</p>
						<div className="restaurant-profile-form">
							<label>
								First name
								<input
									value={claimForm.firstName}
									onChange={(event) =>
										setClaimForm((prev) => ({
											...prev,
											firstName: event.target.value,
										}))
									}
								/>
							</label>
							<label>
								Last name
								<input
									value={claimForm.lastName}
									onChange={(event) =>
										setClaimForm((prev) => ({
											...prev,
											lastName: event.target.value,
										}))
									}
								/>
							</label>
							<label>
								Email
								<input
									type="email"
									value={claimForm.email}
									onChange={(event) =>
										setClaimForm((prev) => ({
											...prev,
											email: event.target.value,
										}))
									}
								/>
							</label>
							<label>
								Phone
								<input
									value={claimForm.phoneNumber}
									onChange={(event) =>
										setClaimForm((prev) => ({
											...prev,
											phoneNumber: event.target.value,
										}))
									}
								/>
							</label>
						</div>
						<label className="check-field">
							<input
								type="checkbox"
								checked={claimForm.emailOwner}
								onChange={(event) =>
									setClaimForm((prev) => ({
										...prev,
										emailOwner: event.target.checked,
									}))
								}
							/>
							Email setup link
						</label>
						<label className="check-field">
							<input
								type="checkbox"
								checked={claimForm.enableScerv}
								onChange={(event) =>
									setClaimForm((prev) => ({
										...prev,
										enableScerv: event.target.checked,
									}))
								}
							/>
							Enable Scerv operational tools now
						</label>
						<p className="owner-claim-help">
							Unchecked means claimed discovery profile: reviews stay live, but
							ordering, reservations, QR check-in, rewards, payments, and host
							check-in stay locked until Scerv enables them later.
						</p>
						<button type="submit" disabled={saving}>
							{saving
								? "Saving..."
								: restaurant.isClaimed
									? "Update Owner Claim"
									: "Claim Restaurant"}
						</button>
					</form>
					<button type="button" onClick={resendOwnerSetup} disabled={saving}>
						{saving ? "Generating..." : "Resend Setup Link"}
					</button>
					{setupResult && (
						<label className="setup-link-box">
							Setup link
							<textarea readOnly value={setupResult.resetLink || ""} />
						</label>
					)}
				</section>
			)}

			{activeTab === "audit" && (
				<section className="restaurant-panel">
					<h2>Recent Admin Audit</h2>
					<table className="restaurant-table">
						<thead><tr><th>Action</th><th>Actor</th><th>When</th></tr></thead>
						<tbody>
							{(profile?.auditLogs || []).map((log) => (
								<tr key={log.id}>
									<td>{log.action}</td>
									<td>{log.actorUid}</td>
									<td>{formatDate(log.createdAt)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			)}
		</div>
	);
};

const RestaurantDetails = () => {
	const { id } = useParams();
	const location = useLocation();
	const [, refreshEnvironment] = useState(0);
	useEffect(() => {
		const changed = event => {
			if (event.key === ADMIN_ENV_STORAGE_KEY || event.key === null) refreshEnvironment(value => value + 1);
		};
		window.addEventListener("storage", changed);
		return () => window.removeEventListener("storage", changed);
	}, []);
	const scope = supportSearchEnvironment(auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey());
	const destination = scope ? readSupportSearchDestination(location.search, {
		firebaseProject: selectedAdminEnvironment.projectId, restaurantId: id,
	}) : null;
	return <RestaurantDetailsContent key={`${id}:${scope}:${destination?.initialTab || "overview"}:${destination?.initialSection || ""}`}
		id={id} initialTab={destination?.initialTab} initialSection={destination?.initialSection} />;
};

export default RestaurantDetails;
